import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Send, Sparkles, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { streamChat } from "../lib/api";
import { LogoMark } from "./Logo";
import { EASE } from "./motion";
import { SITE } from "../lib/site";

const GREETING = {
  role: "assistant",
  content:
    "Hello — I am the Upgrade Law AI assistant. Ask me general questions about Indian business law: trademarks, contracts, company setup, property diligence, licences or disputes.\n\nThis is general information, not legal advice. For your specific matter, use the enquiry form or call +91 80157 82116.",
};

const SUGGESTIONS = [
  "How do I register a trademark in India?",
  "What should I check before signing a lease?",
  "I received a cheque bounce notice — what now?",
  "LLP or private limited — which suits me?",
];

const initSession = () => {
  const existing = localStorage.getItem("ul-chat-session");
  if (existing) return existing;
  const id = crypto.randomUUID();
  localStorage.setItem("ul-chat-session", id);
  return id;
};

export const ChatWidget = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([GREETING]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef(null);
  const sessionId = useRef(null);
  if (sessionId.current === null) sessionId.current = initSession();

  useEffect(() => {
    const handler = () => setOpen((v) => !v);
    window.addEventListener("ul:chat-toggle", handler);
    return () => window.removeEventListener("ul:chat-toggle", handler);
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (preset) => {
    const content = (preset ?? input).trim();
    if (!content || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content }, { role: "assistant", content: "" }]);
    setStreaming(true);
    try {
      await streamChat({ session_id: sessionId.current, message: content }, (token) => {
        setMessages((m) => {
          const next = [...m];
          next[next.length - 1] = {
            role: "assistant",
            content: next[next.length - 1].content + token,
          };
          return next;
        });
      });
    } catch (err) {
      toast.error(err.message || "The assistant is unavailable right now.");
      setMessages((m) => {
        const next = [...m];
        next[next.length - 1] = {
          role: "assistant",
          error: true,
          content: `I could not process that right now. Please try again, or reach the practice directly at ${SITE.phoneDisplay}.`,
        };
        return next;
      });
    }
    setStreaming(false);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="chat-panel"
          initial={{ opacity: 0, y: 40, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 40, scale: 0.96 }}
          transition={{ duration: 0.4, ease: EASE }}
          className="fixed inset-x-4 bottom-40 z-[70] flex max-h-[62vh] flex-col overflow-hidden border border-white/15 bg-white shadow-[0_40px_90px_-30px_rgba(11,21,40,0.6)] sm:inset-x-auto sm:right-5 sm:w-[380px]"
          role="dialog"
          aria-label="AI legal assistant"
        >
          <div className="flex items-center justify-between bg-navy px-5 py-4">
            <div className="flex items-center gap-3">
              <LogoMark className="h-8 w-8" />
              <div>
                <p className="font-serif text-lg leading-none text-white">Upgrade Law Assistant</p>
                <p className="mt-1 flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.18em] text-gold">
                  <Sparkles size={10} /> AI · General info only
                </p>
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              data-testid="chat-close-button"
              aria-label="Close chat"
              className="flex h-8 w-8 items-center justify-center rounded-full border border-white/20 text-white/70 transition-colors hover:border-gold hover:text-gold"
            >
              <X size={15} />
            </button>
          </div>

          <div
            data-testid="chat-messages"
            data-lenis-prevent
            className="flex-1 space-y-4 overflow-y-auto bg-canvas px-5 py-5"
          >
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[85%] rounded-md px-4 py-3 text-[13px] leading-relaxed whitespace-pre-wrap ${
                    m.role === "user"
                      ? "bg-navy text-white"
                      : m.error
                        ? "border border-destructive/30 bg-destructive/5 text-ink"
                        : "border border-line bg-white text-ink"
                  }`}
                >
                  {m.content.replace(/\*\*/g, "")}
                  {streaming && i === messages.length - 1 && m.role === "assistant" && m.content === "" && (
                    <span className="inline-flex gap-1 py-1" aria-label="Assistant is typing">
                      {[0, 1, 2].map((d) => (
                        <span
                          key={d}
                          className="h-1.5 w-1.5 animate-pulse-dot rounded-full bg-gold-dark"
                          style={{ animationDelay: `${d * 0.25}s` }}
                        />
                      ))}
                    </span>
                  )}
                </div>
              </div>
            ))}
            {messages.length === 1 && (
              <div className="flex flex-wrap gap-2 pt-1">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    data-testid={`chat-suggestion-${s.slice(0, 20).toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => send(s)}
                    className="rounded-full border border-gold/50 bg-white px-3.5 py-2 text-left text-[11px] font-medium text-navy transition-all duration-300 hover:bg-gold/10 hover:border-gold"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
            <div ref={bottomRef} />
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              send();
            }}
            className="border-t border-line bg-white p-3"
          >
            <div className="flex items-center gap-2">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                data-testid="chat-input"
                placeholder="Ask a general legal question…"
                aria-label="Chat message"
                maxLength={2000}
                className="field-input !border-transparent !bg-canvas !py-3 focus:!border-gold"
              />
              <button
                type="submit"
                data-testid="chat-send-button"
                disabled={streaming || !input.trim()}
                aria-label="Send message"
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-sm bg-navy text-gold transition-all duration-300 hover:bg-navy-surface disabled:cursor-not-allowed disabled:opacity-40"
              >
                <Send size={16} />
              </button>
            </div>
            <p className="mt-2 px-1 text-[10px] leading-relaxed text-steel/60">
              AI-generated general information — not legal advice, and no advocate-client
              relationship is created here. Do not share confidential documents.
            </p>
          </form>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ChatWidget;
